// Colour maths: hex ↔ sRGB, WCAG 2.x relative luminance and contrast, and OKLCH for perceptually
// even lightness adjustments (derived hover/pressed states, dark-mode brand variants).

export interface Rgb {
  r: number; // 0..1, gamma-encoded sRGB
  g: number;
  b: number;
  a: number; // 0..1
}

const HEX_RE = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i;

export function isHex(value: string): boolean {
  return HEX_RE.test(value);
}

export function parseHex(hex: string): Rgb {
  const m = HEX_RE.exec(hex);
  if (!m) throw new RangeError(`not a #RRGGBB or #RRGGBBAA colour: ${hex}`);
  const n = parseInt(m[1]!, 16);
  return {
    r: ((n >> 16) & 255) / 255,
    g: ((n >> 8) & 255) / 255,
    b: (n & 255) / 255,
    a: m[2] ? parseInt(m[2], 16) / 255 : 1,
  };
}

export function toHex(c: Rgb): string {
  const h = (x: number) =>
    Math.round(Math.min(1, Math.max(0, x)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${h(c.r)}${h(c.g)}${h(c.b)}${c.a < 1 ? h(c.a) : ''}`.toUpperCase();
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c: number) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);

/** WCAG 2.x relative luminance of an opaque colour. */
export function luminance(hex: string): number {
  const c = parseHex(hex);
  if (c.a < 1) throw new RangeError(`contrast needs an opaque colour, got ${hex}`);
  return 0.2126 * toLinear(c.r) + 0.7152 * toLinear(c.g) + 0.0722 * toLinear(c.b);
}

/** WCAG contrast ratio, 1..21. */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

export interface Oklch {
  l: number; // 0..1
  c: number; // chroma, 0..~0.37
  h: number; // degrees
}

export function hexToOklch(hex: string): Oklch {
  const { r, g, b } = parseHex(hex);
  const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)];
  const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const h = (Math.atan2(B, A) * 180) / Math.PI;
  return { l: L, c: Math.hypot(A, B), h: h < 0 ? h + 360 : h };
}

function oklchToLinear({ l, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos((h * Math.PI) / 180);
  const B = c * Math.sin((h * Math.PI) / 180);
  const l_ = (l + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m_ = (l - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s_ = (l - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

const inGamut = (rgb: [number, number, number]) => rgb.every((x) => x >= -1e-6 && x <= 1 + 1e-6);

/** OKLCH → hex, reducing chroma (keeping lightness and hue) until it fits sRGB. */
export function oklchToHex(c: Oklch): string {
  let lo = 0;
  let hi = c.c;
  let rgb = oklchToLinear(c);
  if (!inGamut(rgb)) {
    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToLinear({ ...c, c: mid }))) lo = mid;
      else hi = mid;
    }
    rgb = oklchToLinear({ ...c, c: lo });
  }
  return toHex({ r: fromLinear(rgb[0]), g: fromLinear(rgb[1]), b: fromLinear(rgb[2]), a: 1 });
}

/** Shifts OKLCH lightness by delta (−1..1), clamped. */
export function shiftLightness(hex: string, delta: number): string {
  const c = hexToOklch(hex);
  return oklchToHex({ ...c, l: Math.min(1, Math.max(0, c.l + delta)) });
}

/** Applies an alpha (0..1) to an opaque hex colour. */
export function withAlpha(hex: string, alpha: number): string {
  return toHex({ ...parseHex(hex), a: alpha });
}

/** sRGB mix like CSS color-mix(in srgb, a p, b): p is the weight of a (0..1). */
export function mix(a: string, b: string, p: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  return toHex({
    r: x.r * p + y.r * (1 - p),
    g: x.g * p + y.g * (1 - p),
    b: x.b * p + y.b * (1 - p),
    a: x.a * p + y.a * (1 - p),
  });
}

/**
 * The nearest colour to fg (same hue, OKLCH lightness only, searching both directions) for which
 * test passes; by default, contrast with bg of at least min. Used for brand suggestions and for
 * proposing fixes to a design direction.
 */
export function nearestPassing(
  fg: string,
  test: number | ((candidate: string) => boolean),
  bg?: string,
): string | undefined {
  const ok = typeof test === 'number' ? (c: string) => contrast(c, bg!) >= test : test;
  if (ok(fg)) return fg;
  const base = hexToOklch(fg);
  for (let step = 1; step <= 400; step++) {
    for (const sign of [-1, 1]) {
      const l = base.l + sign * step * 0.0025;
      if (l < 0 || l > 1) continue;
      const candidate = oklchToHex({ ...base, l });
      if (ok(candidate)) return candidate;
    }
  }
  return undefined;
}
